import { useCallback } from 'react';
import { useAuthStore } from './token-store';
import { AppRole } from './roles';
import { authService } from './authService';

// Stable empty array so the zustand selector keeps referential equality (avoids render loops).
const EMPTY_ROLES: AppRole[] = [];

export function useAuth() {
  const session = useAuthStore((s) => s.session);
  const status = useAuthStore((s) => s.status);
  return {
    session,
    status, // 'loading' | 'authenticated' | 'unauthenticated'
    isAuthenticated: status === 'authenticated' && !!session?.accessToken,
    email: session?.email ?? null,
    name: session?.name ?? null,
    roles: session?.roles ?? EMPTY_ROLES,
  };
}

export function useRoles(): AppRole[] {
  return useAuthStore((s) => s.session?.roles ?? EMPTY_ROLES);
}

// CONTRACTS.md §9 — the guards call `useHasAnyRole(roles)` with the array of allowed roles.
export function useHasAnyRole(allowed: AppRole[]): boolean {
  const roles = useRoles();
  return allowed.length === 0 ? true : roles.some((r) => allowed.includes(r));
}

export function useLogout(): () => void {
  return useCallback(() => {
    authService.logout();
  }, []);
}

// doc 04 imports `useSession` — same shape as useAuth.
export const useSession = useAuth;
